import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { Dispatch } from 'redux';
import { useEffect } from 'react';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { FetchError } from '../../Interfaces/FetchError';
import clsx from 'clsx';
import { Button } from '@progress/kendo-react-buttons';
import {
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
  Zoom,
} from '@material-ui/core';
import { resetAssetStatusesFetchError } from '../../store/Devices/AssetStatesActions';
import { ReportQueryResult } from '../../Interfaces/Reports/ReportQueryResult';
import ReportsSection from './ReportsSection';
import { getLocaleString } from '../../utils/utils';
import ReportsSelection from './ReportsSelection';
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  fetchReportDataByNode,
  fetchReportDataByStore,
  exportReportData,
} from '../../store/Reports/ReportsActions';
import { ReportFilter } from '../../Interfaces/Reports/ReportFilter';
import { ReportQueryInfoByNode } from '../../Interfaces/Reports/ReportQueryInfoByNode';
import { ReportQueryInfoByStore } from '../../Interfaces/Reports/ReportQueryInfoByStore';
import { ReportCategory } from '../../Interfaces/Reports/ReportCategory';
import { EnterpriseTooltip } from '../EnterpriseTooltip';
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import ReportsGeneratedDateTimeGridItem from './ReportsGeneratedDateTimeGridItem';
import ReportsLoadingPanel from './ReportsLoadingPanel';
import { ReportExportRequestBody } from '../../Interfaces/Reports/ReportExportRequestBody';
import { ReportExportResult } from '../../Interfaces/Reports/ReportExportResult';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridAlign: {
      display: 'block',
      width: '75vw',
    },
    margin: {
      margin: theme.spacing(1),
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    toolbar: {
      height: 65,
      alignItems: 'flex-end',
      padding: theme.spacing(1),
      gap: 10,
    },
    toolbarTextbox: {
      height: 40,
    },
    toolbarButton: {
      height: 40,
    },
    container: {
      display: 'grid',
      padding: theme.spacing(1),
      margin: theme.spacing(1),
    },
    title: {
      fontSize: '1.1rem',
      display: 'inline',
      fontWeight: 'bold',
    },
    subTitle: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    section: {
      paddingLeft: 15,
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    dialogTextBold: {
      fontWeight: 'bold',
    },
    generateButton: {
      width: 150,
      margin: 10,
    },
    reportsSectionContainer: {
      border: '1px solid lightgrey',
      width: 'max-content',
    },
  }),
);

const ReportsContainer: React.FC = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token,
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const reportData: ReportQueryResult = useSelector(
    (state: ApplicationState) => state.reportsReducer.reportData,
  );

  const reportCategories: ReportCategory[] = useSelector(
    (state: ApplicationState) => state.reportsReducer.categories,
  );

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.reportsReducer.fetchError,
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const reportFilter: ReportFilter = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.reportFilter,
  );

  const isRequestingReportExportData: boolean = useSelector(
    (state: ApplicationState) =>
      state.reportsReducer.isRequestingReportExportData,
  );

  const reportExportResult: ReportExportResult = useSelector(
    (state: ApplicationState) => state.reportsReducer.reportExportResult,
  );

  const [exportingExcel, setExportingExcel] = React.useState<boolean>(false);

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const REPORT_DATE_RANGE_TOOLTIP = GetString('REPORT_DATE_RANGE_TOOLTIP');

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  useEffect(() => {
    if (reportExportResult.Code == 1) setExportingExcel(false);
  }, [reportExportResult]);

  const [error, displayError] = React.useState(false);

  const clearFetchError = () => {
    displayError(false);
    resetAssetStatusesFetchError(dispatch);
  };

  const exportReportToExcel = async () => {
    setExportingExcel(true);

    let newReportFilter: ReportFilter = JSON.parse(
      JSON.stringify(reportFilter),
    );

    let query: ReportExportRequestBody = {
      ReportID: reportData.ReportID,
      SiteNumbers: reportFilter.SiteNumbers,
      LaneNumbers: reportFilter.LaneNumbers,
      ReportStartDateTime: newReportFilter.StartDateTime,
      ReportEndDateTime: newReportFilter.EndDateTime,
      RequestedOn: new Date(),
      Language: selectedLanguage,
      ExportPeriodBreakdown: reportData.ExportPeriodBreakdown.toString(),
    };

    exportReportData(dispatch, query);
  };

  const refreshOnClicked = () => {
    let newReportFilter: ReportFilter = JSON.parse(
      JSON.stringify(reportFilter),
    );

    if (
      reportFilter.SiteNumbers.length > 1 ||
      reportFilter.LaneNumbers.length === 0
    ) {
      let query: ReportQueryInfoByStore = {
        ReportID: reportData.ReportID,
        SiteNumbers: reportFilter.SiteNumbers,
        StartDateTime: newReportFilter.StartDateTime,
        EndDateTime: newReportFilter.EndDateTime,
        Language: selectedLanguage,
      };
      fetchReportDataByStore(dispatch, query);
    } else {
      let query: ReportQueryInfoByNode = {
        ReportID: reportData.ReportID,
        SiteNumber: reportFilter.SiteNumbers[0],
        LaneNumbers: reportFilter.LaneNumbers,
        StartDateTime: newReportFilter.StartDateTime,
        EndDateTime: newReportFilter.EndDateTime,
        Language: selectedLanguage,
      };

      fetchReportDataByNode(dispatch, query);
    }
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <div>
            <ReportsSelection />
          </div>
          {<ReportsLoadingPanel exporting={isRequestingReportExportData} />}
          <hr></hr>
          {reportData.ReportID ? (
            <Grid
              container
              spacing={2}
              className={clsx(classes.gridAlign, classes.margin)}
            >
              <Grid item md={6} xs={12}>
                <Button
                  svgIcon={arrowRotateCwIcon}
                  onClick={refreshOnClicked}
                  type="submit"
                  className={classes.margin}
                >
                  <StringResourceManager stringKey={'REFRESH'} />
                </Button>
                <Button
                  onClick={exportReportToExcel}
                  type="submit"
                  className={classes.margin}
                >
                  <StringResourceManager stringKey={'EXPORT_TO_EXCEL'} />
                </Button>
              </Grid>
              <ReportsGeneratedDateTimeGridItem />
              <Grid item md={12} xs={12}>
                <StringResourceManager
                  stringKey={'START_DATE_TIME'}
                  variant="body1"
                  style="dialogLabel"
                  gutterBottom={true}
                />
                <EnterpriseTooltip
                  title={REPORT_DATE_RANGE_TOOLTIP}
                  placement="right"
                  TransitionComponent={Zoom}
                >
                  <Typography
                    variant="body1"
                    className={clsx(
                      classes.dialogLabel,
                      classes.dialogTextBold,
                    )}
                    gutterBottom
                  >
                    {getLocaleString(reportData.StartDateTime)}
                  </Typography>
                </EnterpriseTooltip>
              </Grid>
              <Grid item md={12} xs={12}>
                <StringResourceManager
                  stringKey={'END_DATE_TIME'}
                  variant="body1"
                  style="dialogLabel"
                  gutterBottom={true}
                />
                <EnterpriseTooltip
                  title={REPORT_DATE_RANGE_TOOLTIP}
                  placement="right"
                  TransitionComponent={Zoom}
                >
                  <Typography
                    variant="body1"
                    className={clsx(
                      classes.dialogLabel,
                      classes.dialogTextBold,
                    )}
                    gutterBottom
                  >
                    {getLocaleString(reportData.EndDateTime)}
                  </Typography>
                </EnterpriseTooltip>
              </Grid>
            </Grid>
          ) : null}

          <div className={classes.reportsSectionContainer}>
            {reportData?.Sections?.length > 0
              ? reportData.Sections.map((s, index) => {
                  return (
                    <div className={classes.container} key={index}>
                      {s.Title ? (
                        <Typography className={classes.title}>
                          {s.Title}
                        </Typography>
                      ) : null}
                      {s.Subtitle ? (
                        <Typography className={classes.subTitle}>
                          {s.Subtitle}
                        </Typography>
                      ) : null}
                      <div className={classes.section}>
                        <ReportsSection section={s} key={index} />
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default ReportsContainer;
